import { getAuth } from 'firebase/auth';
import type { ProductFormData, ProductSpecification } from '../types/product';
import { generateSlug } from '../utils/slug';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8787';

/**
 * Get Firebase auth token
 */
async function getAuthToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  return await user.getIdToken();
}

/**
 * Converte specifications do formato GitHub (objeto de objetos) para o formato do admin (array)
 * Suporta tanto o formato antigo (apenas value) quanto o novo (label + value)
 */
function convertSpecificationsFromGitHub(
  specs:
    | Record<string, { label: Record<string, string>; value: Record<string, string> }>
    | undefined
): ProductSpecification[] {
  if (!specs || typeof specs !== 'object') return [];

  return Object.entries(specs).map(([key, spec], index) => {
    // Verifica se é o formato novo (com label e value separados)
    if (spec && typeof spec === 'object' && 'label' in spec && 'value' in spec) {
      return {
        id: key,
        label: spec.label || { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
        value: spec.value || { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
        order: index,
      };
    }

    // Formato antigo (spec é o próprio value, sem label)
    // Usa o value tanto para label quanto para value (compatibilidade)
    return {
      id: key,
      label: spec || { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
      value: spec || { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
      order: index,
    };
  }) as ProductSpecification[];
}

/**
 * Converte specifications do formato do admin (array) para o formato GitHub (objeto de objetos)
 */
function convertSpecificationsToGitHub(
  specs: ProductSpecification[] | undefined
): Record<string, { label: Record<string, string>; value: Record<string, string> }> | undefined {
  if (!specs || specs.length === 0) return undefined;

  return specs.reduce(
    (acc, spec) => {
      acc[spec.id] = {
        label: spec.label as Record<string, string>,
        value: spec.value as Record<string, string>,
      };
      return acc;
    },
    {} as Record<string, { label: Record<string, string>; value: Record<string, string> }>
  );
}

interface GitHubFileResponse {
  content: string;
  sha: string;
  path: string;
}

interface GitHubProduct extends Omit<ProductFormData, 'specifications'> {
  specifications?: Record<string, { label: Record<string, string>; value: Record<string, string> }>;
}

class GitHubService {
  /**
   * Obtém o conteúdo de um arquivo do repositório
   */
  async getFile(path: string): Promise<GitHubFileResponse | null> {
    try {
      const token = await getAuthToken();
      const url = `${API_BASE_URL}/api/github/file?path=${encodeURIComponent(path)}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.file;
    } catch (error) {
      console.error('Error fetching file:', error);
      throw error;
    }
  }

  /**
   * Cria ou atualiza um arquivo no repositório
   */
  async saveFile(path: string, content: string, message: string): Promise<void> {
    try {
      const token = await getAuthToken();
      const url = `${API_BASE_URL}/api/github/file`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path, content, message }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`API error: ${error.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving file:', error);
      throw error;
    }
  }

  /**
   * Deleta um arquivo do repositório
   */
  async deleteFile(path: string, message: string): Promise<void> {
    try {
      const token = await getAuthToken();
      const url = `${API_BASE_URL}/api/github/file?path=${encodeURIComponent(path)}&message=${encodeURIComponent(message)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`API error: ${error.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Lista todos os arquivos de uma categoria
   */
  async listProducts(category: string): Promise<ProductFormData[]> {
    const url = `${API_BASE_URL}/api/github/products/${category}`;
    try {
      const token = await getAuthToken();

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      const products = data.products || [];

      // Converter specifications do formato GitHub para o formato do admin
      return products.map((product: GitHubProduct) => ({
        ...product,
        specifications: product.specifications
          ? convertSpecificationsFromGitHub(product.specifications)
          : undefined,
      })) as ProductFormData[];
    } catch (error) {
      console.error(`Error listing products from ${url}:`, error);
      return [];
    }
  }

  /**
   * Salva um produto no repositório
   */
  async saveProduct(product: ProductFormData): Promise<void> {
    const slug = product.slug || generateSlug(product.name['pt-BR']);
    // Converter specifications para o formato do GitHub antes de salvar
    const productForGitHub = {
      ...product,
      slug,
      specifications: convertSpecificationsToGitHub(product.specifications),
    };
    const path = `data/products/${product.category}/${slug}.json`;
    const content = JSON.stringify(productForGitHub, null, 2);
    const message = product.id ? `chore: update product ${slug}` : `feat: add new product ${slug}`;

    await this.saveFile(path, content, message);
  }

  /**
   * Deleta um produto do repositório
   */
  async deleteProduct(category: string, slug: string): Promise<void> {
    const path = `data/products/${category}/${slug}.json`;
    const message = `chore: delete product ${slug}`;
    await this.deleteFile(path, message);
  }
}

export const githubService = new GitHubService();
